import React, { FC, useState, useEffect, useRef } from "react"
import { observer } from "mobx-react-lite"
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ImageBackground,
  Platform,
  StatusBar,
  Modal,
  SafeAreaView,
  useWindowDimensions,
  Alert,
} from "react-native"
import { MaterialIcons, FontAwesome5, MaterialCommunityIcons, Ionicons } from "@expo/vector-icons"

import { HomeStackParamList } from "../navigators"
import { DrawerScreenProps } from "@react-navigation/drawer"
import { useStores } from "../models"
import * as FileSystem from 'expo-file-system'
import * as ImagePicker from 'expo-image-picker'
import * as ImageManipulator from 'expo-image-manipulator'
import SignatureCanvas from 'react-signature-canvas'
import SignatureScreen from 'react-native-signature-canvas'
import { convertImageToBase64 } from "../utils/general"
import { ModalFull } from "../components"
import { customApp } from "../config"
import * as Storage from "../utils/storage"
import { calcularVelocidadConexion } from "../utils/general"

// --- Componentes Reutilizables Nativo ---

const SectionHeader = ({ icon, name, title, library: IconComponent = MaterialIcons }: any) => (
  <View style={styles.sectionHeader}>
    <IconComponent name={name} size={18} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
    <Text style={styles.sectionTitle}>{title}</Text>
  </View>
)

const RowItem = ({ children, style }: any) => (
  <View style={[styles.rowItem, style]}>{children}</View>
)

const CustomSelect = ({ value, onValueChange, options, placeholder = "Seleccionar..." }: any) => {
  const [modalVisible, setModalVisible] = useState(false)
  const selectedOption = options.find((opt: any) => opt.value === value)

  return (
    <>
      <TouchableOpacity style={styles.selectInput} onPress={() => setModalVisible(true)}>
        <Text style={styles.selectText}>{selectedOption ? selectedOption.label : placeholder}</Text>
        <MaterialIcons name="arrow-drop-down" size={24} color="#666" />
      </TouchableOpacity>

      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setModalVisible(false)}>
          <View style={styles.selectModalContainer}>
            <ScrollView style={{ maxHeight: 300 }}>
              {options.map((opt: any) => (
                <TouchableOpacity
                  key={opt.value}
                  disabled={opt.disabled}
                  style={[
                    styles.selectOption,
                    opt.disabled && { opacity: 0.4 },
                    opt.value === value && { backgroundColor: "#e8e0f8" },
                  ]}
                  onPress={() => {
                    onValueChange(opt.value)
                    setModalVisible(false)
                  }}
                >
                  <Text style={[styles.selectOptionText, opt.value === value && { color: "#6200EE", fontWeight: "bold" }]}>
                    {opt.label}
                  </Text>
                  {opt.value === value && <Ionicons name="checkmark" size={18} color="#6200EE" />}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  )
}

// --- Pantalla Principal ---

export const SettingsScreen: FC<DrawerScreenProps<HomeStackParamList, "Settings">> = observer(function SettingsScreen(props) {
  const { authenticationStore, formsStore, statusStore, integrationsStore } = useStores()
  const route = props.route
  const navigation = props.navigation
  const user = authenticationStore.userInfo.user
  const source = user?.hasProfile ? { uri: user.profileUrl } : require("../../assets/images/logo_circle.png")

  const [modalVisible, setModalVisible] = useState(false)
  const [speedTest, setSpeedTest] = useState("")
  const [freeSpaceGB, setFreeSpaceGB] = useState<string | null>(null)
  const [dbS3, setDbS3] = useState<any[]>([])
  const [mmkv, setMMKV] = useState<any>({})

  const signRef = useRef<any>(null)

  const screen = useWindowDimensions()
  let width = statusStore.windowDimensions.width - 15
  let height = (statusStore.windowDimensions.height - 150) > width ? width * 0.66 : (statusStore.windowDimensions.height - 150)

  const clear = () => {
    Platform.OS === "web" ? signRef.current?.clear() : signRef.current?.clearSignature()
  }

  const getFromCanvas = async () => {
    if (Platform.OS === "web") {
      let uriBase64 = signRef.current?.getCanvas().toDataURL("image/jpeg", 1)
      let resizeTo = [{ resize: { width: 400 } }]
      uriBase64 = await ImageManipulator.manipulateAsync(
        uriBase64, resizeTo,
        { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
      )
      save(uriBase64.uri)
    } else {
      signRef.current?.readSignature()
    }
  }

  const save = async (uri: string) => {
    authenticationStore.saveSignCache(uri)
    setModalVisible(false)
  }

  const putStamp = () => {
    if (Platform.OS === "web") {
      signRef.current?.fromDataURL(authenticationStore.userInfo.user.stampCache)
    } else {
      convertImageToBase64(authenticationStore.userInfo.user.stampCache, signRef.current?.loadDataURL)
    }
  }

  const chooseFile = async (destination: "profile" | "stamp" = "profile") => {
    let result: ImagePicker.ImagePickerResult = await ImagePicker.launchImageLibraryAsync({
      allowsEditing: true,
      quality: 1,
    })

    if (!result.canceled && result.assets && result.assets.length > 0) {
      const selectedUri = result.assets[0].uri
      let resizeTo = [{ resize: { width: 400 } }]
      const manipulated = await ImageManipulator.manipulateAsync(
        selectedUri, resizeTo,
        { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
      )

      switch (destination) {
        case "stamp":
          authenticationStore.saveStampCache(manipulated.uri)
          break
        case "profile":
          if (Platform.OS === "web") {
            authenticationStore.mustUploadProfileImage(true, manipulated.uri)
          } else {
            await FileSystem.copyAsync({
              from: manipulated.uri,
              to: `${FileSystem.documentDirectory}profile-${authenticationStore.userInfo.user.id}`.concat(manipulated.uri.substring(manipulated.uri.lastIndexOf(".")))
            })
          }
          break
      }
    } else {
      Alert.alert('Aviso', 'No seleccionaste ninguna imagen.')
    }
  }

  const checkFreeSpace = async () => {
    try {
      const BYTES_TO_GB = 1073741824
      const freeBytes = await FileSystem.getFreeDiskStorageAsync()
      const freeGB = freeBytes / BYTES_TO_GB
      setFreeSpaceGB(freeGB.toFixed(2))
    } catch (err) {
      console.error("Error al obtener el espacio en disco:", err)
    }
  }

  const deleteS3 = async (integration: any) => {
    const basePATH = `${FileSystem.documentDirectory}${integration.definition.tenantId}_${integration.tableName}.sqlite`
    await FileSystem.deleteAsync(basePATH.concat(".zip"), { idempotent: true })
    await FileSystem.deleteAsync(basePATH.concat(".lastSync"), { idempotent: true })
    FileSystem.deleteAsync(integration.path, { idempotent: true }).then(() => {
      integrationsStore.getS3Integrations().then((lst: any) => setDbS3(lst))
    })
  }

  const refreshData = async () => {
    try {
      checkFreeSpace()
      integrationsStore.getS3Integrations().then((lst: any) => setDbS3(lst))
      const PATH = `${FileSystem.documentDirectory}mmkv/mmkv.default`
      FileSystem.getInfoAsync(PATH).then((info) => setMMKV(info))
    } catch (err) {
      console.log("Error refreshing data", err)
    }
  }

  useEffect(() => {
    refreshData()
  }, [])

  const bgTask = authenticationStore.userInfo.user.bgTask || {}

  const style = `.m-signature-pad {box-shadow: none; margin-top:0px;margin-left:1px; } 
  .m-signature-pad--body {left:0}
  .m-signature-pad--footer {display: none; margin: 0px;}
  body,html { width: ${width.toFixed(0)}px; height: ${height.toFixed(0)}px;}`

  let leftButton = (
    <TouchableOpacity onPress={() => navigation.openDrawer()} style={styles.headerIconButton}>
      <MaterialIcons name="menu" size={26} color="white" />
    </TouchableOpacity>
  )
  if (route.params?.fromForm) {
    leftButton = (
      <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerIconButton}>
        <MaterialIcons name="arrow-back" size={26} color="white" />
      </TouchableOpacity>
    )
  } else if (route.params?.navigateTo) {
    leftButton = (
      <TouchableOpacity onPress={() => navigation.navigate(route.params?.navigateTo)} style={styles.headerIconButton}>
        <MaterialIcons name="arrow-back" size={26} color="white" />
      </TouchableOpacity>
    )
  }

  const timeOptions = Array.from({ length: 24 }, (_, index) => ({
    label: `00${index}`.slice(-2),
    value: `00${index}`.slice(-2),
  }))

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar backgroundColor={customApp.styles.primaryColor} barStyle="light-content" />
      
      {/* Header */}
      <View style={styles.header}>
        {leftButton}
        <Text style={styles.headerTitle}>Configuraciones</Text>
      </View>

      <ScrollView style={styles.body}>
        {/* Perfil de Usuario */}
        <SectionHeader library={FontAwesome5} name="user-circle" title="Perfil de Usuario" />
        <View style={styles.profileContainer}>
          <ImageBackground source={source} style={styles.avatar} imageStyle={{ borderRadius: 40 }} resizeMode="cover">
            <TouchableOpacity onPress={() => chooseFile()} style={styles.cameraOverlay}>
              <MaterialIcons name="camera-alt" size={20} color="white" />
            </TouchableOpacity>
          </ImageBackground>
          <View style={styles.profileDetails}>
            <View style={styles.nameRow}>
              <Text style={styles.userName}>{user?.firstName} {user?.lastName}</Text>
              <TouchableOpacity onPress={() => chooseFile()}>
                <MaterialIcons name="camera-alt" size={18} color="#6200EE" style={{ marginLeft: 6 }} />
              </TouchableOpacity>
            </View>
            {!customApp.user && <Text style={styles.userEmail}>{user?.email}</Text>}
          </View>
        </View>

        {/* Sello */}
        <View style={styles.sectionHeaderBetween}>
          <View style={styles.row}>
            <FontAwesome5 name="stamp" size={16} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
            <Text style={styles.sectionTitle}>Sello</Text>
          </View>
          <View style={styles.row}>
            {user?.stampCache && (
              <TouchableOpacity onPress={() => authenticationStore.saveStampCache(undefined)} style={styles.actionBtn}>
                <Ionicons name="trash" size={20} color="#dc3545" />
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => chooseFile("stamp")} style={styles.actionBtn}>
              <MaterialIcons name="photo-library" size={20} color={customApp.styles.primaryColor} />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.mediaContainer}>
          {user?.stampCache ? (
            <Image source={{ uri: user.stampCache }} style={styles.previewImage} resizeMode="contain" />
          ) : (
            <TouchableOpacity style={styles.emptyButton} onPress={() => chooseFile("stamp")}>
              <Text style={styles.emptyButtonText}>No has cargado ninguna imagen de sello.</Text>
              <MaterialIcons name="photo-library" size={22} color={customApp.styles.primaryColor} />
            </TouchableOpacity>
          )}
        </View>

        {/* Firma Guardada */}
        <View style={styles.sectionHeaderBetween}>
          <View style={styles.row}>
            <FontAwesome5 name="signature" size={16} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
            <Text style={styles.sectionTitle}>Firma Guardada</Text>
          </View>
          <View style={styles.row}>
            {user?.signCache && (
              <TouchableOpacity onPress={() => authenticationStore.saveSignCache(undefined)} style={styles.actionBtn}>
                <Ionicons name="trash" size={20} color="#dc3545" />
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => setModalVisible(true)} style={styles.actionBtn}>
              <MaterialCommunityIcons name="signature-freehand" size={20} color={customApp.styles.primaryColor} />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.mediaContainer}>
          {user?.signCache ? (
            <Image source={{ uri: user.signCache }} style={styles.previewImage} resizeMode="contain" />
          ) : (
            <TouchableOpacity style={styles.emptyButton} onPress={() => setModalVisible(true)}>
              <Text style={styles.emptyButtonText}>No has generado tu firma de perfil aún.</Text>
              <MaterialCommunityIcons name="signature-freehand" size={22} color={customApp.styles.primaryColor} />
            </TouchableOpacity>
          )}
        </View>

        {/* Consumo / Sincronización */}
        {Platform.OS === "web" ? (
          <SectionHeader library={MaterialCommunityIcons} name="database" title={`Consumo Almacenamiento: ${Storage.localStorageSpace()}`} />
        ) : (
          <>
            <View style={styles.sectionHeaderBetween}>
              <View style={[styles.row, { flex: 1 }]}>
                <MaterialIcons name="settings-backup-restore" size={18} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
                <Text style={styles.sectionTitle}>Subir Items Cerrados</Text>
                <MaterialCommunityIcons name="wifi" size={16} color="#28a745" style={{ marginLeft: 6 }} />
                {bgTask.uploadItemsNetwork === "dataOrWifi" && (
                  <MaterialCommunityIcons name="signal-off" size={16} color="#28a745" style={{ marginLeft: 2 }} />
                )}
              </View>
              <MaterialCommunityIcons
                name={bgTask.uploadItems ? "cog-play" : "cog-stop"}
                size={22}
                color={bgTask.uploadItems ? "#28a745" : "#dc3545"}
              />
            </View>

            <View style={styles.subContainer}>
              <View style={styles.rowBetween}>
                <MaterialCommunityIcons name="clock-start" size={18} color="#555" />
                <CustomSelect
                  value={bgTask.uploadItemsFromTime}
                  options={timeOptions.map((opt) => ({
                    ...opt,
                    disabled: parseInt(opt.value) <= parseInt(bgTask.uploadItemsToTime || "0"),
                  }))}
                  onValueChange={(val: string) => authenticationStore.updateBgTask({ uploadItemsFromTime: val })}
                />
                <CustomSelect
                  value={bgTask.uploadItemsToTime}
                  options={timeOptions.map((opt) => ({
                    ...opt,
                    disabled: parseInt(opt.value) >= parseInt(bgTask.uploadItemsFromTime || "24"),
                  }))}
                  onValueChange={(val: string) => authenticationStore.updateBgTask({ uploadItemsToTime: val })}
                />
                <MaterialCommunityIcons name="clock-end" size={18} color="#555" />
              </View>

              <View style={styles.infoCard}>
                <Ionicons name="warning-outline" size={20} color="#ffc107" style={{ marginRight: 6 }} />
                <Text style={styles.infoCardText}>
                  Este proceso se activa al dejar de utilizar Field y que la aplicación quede en segundo plano, así no interferir en la operatoria.
                </Text>
              </View>
            </View>

            {/* Recordatorios */}
            <View style={styles.sectionHeaderBetween}>
              <View style={styles.row}>
                <Ionicons name="calendar-outline" size={18} color={customApp.styles.primaryColor} style={styles.sectionIcon} />
                <Text style={styles.sectionTitle}>Recordatorio de Tareas próximas</Text>
              </View>
              <MaterialCommunityIcons
                name={bgTask.taskreminder ? "play" : "stop"}
                size={20}
                color={bgTask.taskreminder ? "#28a745" : "#dc3545"}
              />
            </View>

            <View style={[styles.row, { paddingHorizontal: 16, marginVertical: 10 }]}>
              <Text style={{ fontSize: 14, marginRight: 10 }}>Notificar</Text>
              <CustomSelect
                value={bgTask.taskreminderTime}
                options={[
                  { label: "5 minutos antes", value: "5" },
                  { label: "30 minutos antes", value: "30" },
                  { label: "1 Hora antes", value: "60" },
                ]}
                onValueChange={(val: string) => authenticationStore.updateBgTask({ taskreminderTime: val })}
              />
            </View>
          </>
        )}

        {/* Métricas de Sistema */}
        <RowItem>
          <MaterialIcons name="speed" size={18} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
          <Text style={styles.rowLabel}>Velocidad Conexión al Servidor</Text>
          <TouchableOpacity onPress={() => calcularVelocidadConexion(setSpeedTest, statusStore)} style={{ marginHorizontal: 8 }}>
            <MaterialIcons name="refresh" size={22} color={customApp.styles.primaryColor} />
          </TouchableOpacity>
          <Text style={styles.rowValue}>{speedTest ? `${speedTest} Mbps` : "--"}</Text>
        </RowItem>

        <RowItem>
          <MaterialIcons name="settings-overscan" size={18} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
          <Text style={styles.rowLabel}>Resolución disponible</Text>
          <Text style={styles.rowValue}>{statusStore.windowDimensions.width.toFixed()}x{statusStore.windowDimensions.height.toFixed()}</Text>
        </RowItem>

        <View style={styles.sectionHeaderBetween}>
          <View style={styles.row}>
            <MaterialIcons name="settings" size={18} color={customApp.styles.secondaryColor} style={styles.sectionIcon} />
            <Text style={styles.sectionTitle}>Diccionario de Datos</Text>
          </View>
          <TouchableOpacity onPress={() => refreshData()}>
            <MaterialIcons name="refresh" size={22} color={customApp.styles.primaryColor} />
          </TouchableOpacity>
        </View>

        {/* Lista Bases de Datos */}
        <View style={{ paddingHorizontal: 12 }}>
          <View style={styles.tableRow}>
            <MaterialCommunityIcons name="database" size={18} color={mmkv?.exists ? "#0d6efd" : "#dc3545"} style={{ marginRight: 8 }} />
            <Text style={styles.tableName}>Field Database</Text>
            <Text style={styles.tableValue}>{mmkv?.size ? `${(mmkv.size / 1024 / 1024).toFixed(2)} MB` : "0 MB"}</Text>
          </View>

          {dbS3?.map((integration: any) => (
            <View key={integration.id} style={styles.tableRow}>
              <MaterialCommunityIcons name="database" size={18} color={integration.infoFile?.exists ? "#0d6efd" : "#dc3545"} style={{ marginRight: 8 }} />
              <Text style={styles.tableName} numberOfLines={1}>{integration.definition?.name}</Text>
              {integration.infoFile?.exists ? (
                <View style={styles.row}>
                  <Text style={styles.tableValue}>{(integration.infoFile.size / 1024 / 1024).toFixed(2)} MB</Text>
                  <TouchableOpacity onPress={() => deleteS3(integration)} style={{ marginLeft: 8 }}>
                    <MaterialIcons name="delete" size={20} color="#dc3545" />
                  </TouchableOpacity>
                </View>
              ) : (
                <Text style={[styles.tableValue, { color: "#888" }]}>No descargada</Text>
              )}
            </View>
          ))}

          <View style={[styles.tableRow, { backgroundColor: "#d1e7dd", borderBottomWidth: 0, borderRadius: 6, marginTop: 6 }]}>
            <MaterialCommunityIcons name="memory" size={18} color="#0f5132" style={{ marginRight: 8 }} />
            <Text style={[styles.tableName, { color: "#0f5132", fontWeight: "bold" }]}>Espacio Disponible</Text>
            <Text style={[styles.tableValue, { color: "#0f5132", fontWeight: "bold" }]}>{freeSpaceGB ? `${freeSpaceGB} GB` : "--"}</Text>
          </View>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Modal Firma */}
      <ModalFull
        visible={modalVisible}
        title="Firmar"
        closeButton_onPress={() => setModalVisible(!modalVisible)}
        rightButtons={
          user?.stampCache ? (
            <TouchableOpacity onPress={putStamp} style={{ padding: 6 }}>
              <FontAwesome5 name="stamp" color="white" size={18} />
            </TouchableOpacity>
          ) : null
        }
        footer={
          <View style={styles.modalFooter}>
            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: "#0d6efd" }]} onPress={getFromCanvas}>
              <Ionicons name="checkmark" size={18} color="white" style={{ marginRight: 4 }} />
              <Text style={styles.modalBtnText}>Aceptar</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: "#6c757d" }]} onPress={clear}>
              <Ionicons name="close" size={18} color="white" style={{ marginRight: 4 }} />
              <Text style={styles.modalBtnText}>Limpiar</Text>
            </TouchableOpacity>
          </View>
        }
      >
        <View style={{ width: width || 350, alignSelf: "center", borderWidth: 1, borderColor: "#ccc" }}>
          {Platform.OS === "web" ? (
            <SignatureCanvas
              ref={signRef}
              backgroundColor="#ffffff"
              minWidth={1}
              maxWidth={5}
              canvasProps={{ width: width || 350, height: height || 200 }}
            />
          ) : (
            <View style={{ height }}>
              <SignatureScreen ref={signRef} onOK={save} webStyle={style} />
            </View>
          )}
        </View>
      </ModalFull>
    </SafeAreaView>
  )
})

// --- ESTILOS NATIVOS ---

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  header: {
    height: 56,
    backgroundColor: customApp.styles.primaryColor,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  headerIconButton: {
    padding: 8,
  },
  headerTitle: {
    color: "#ffffff",
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: 12,
  },
  body: {
    flex: 1,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  rowBetween: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginTop: 10,
  },
  sectionHeaderBetween: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#f5f5f5",
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginTop: 12,
  },
  sectionIcon: {
    marginRight: 12,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#333333",
  },
  actionBtn: {
    padding: 6,
    marginLeft: 8,
  },
  profileContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  avatar: {
    width: 76,
    height: 76,
    justifyContent: "center",
    alignItems: "center",
  },
  cameraOverlay: {
    backgroundColor: "rgba(0,0,0,0.35)",
    width: 76,
    height: 76,
    borderRadius: 38,
    justifyContent: "center",
    alignItems: "center",
  },
  profileDetails: {
    marginLeft: 16,
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 8,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  userName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#222",
  },
  userEmail: {
    fontSize: 12,
    color: "#666",
    marginTop: 2,
  },
  mediaContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: "center",
  },
  previewImage: {
    width: "100%",
    height: 140,
    backgroundColor: "#fafafa",
    borderRadius: 4,
  },
  emptyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    width: "100%",
  },
  emptyButtonText: {
    color: customApp.styles.primaryColor,
    fontSize: 13,
    marginRight: 8,
  },
  subContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  infoCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff8e1",
    padding: 10,
    borderRadius: 6,
    marginTop: 10,
  },
  infoCardText: {
    fontSize: 12,
    color: "#856404",
    fontStyle: "italic",
    flex: 1,
  },
  rowItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#f5f5f5",
    marginTop: 8,
  },
  rowLabel: {
    fontSize: 14,
    color: "#333",
    flex: 1,
  },
  rowValue: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#444",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  tableName: {
    flex: 1,
    fontSize: 13,
    color: "#333",
  },
  tableValue: {
    fontSize: 13,
    color: "#555",
  },
  selectInput: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 110,
    justifyContent: "space-between",
  },
  selectText: {
    fontSize: 13,
    color: "#333",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
  },
  selectModalContainer: {
    width: "80%",
    backgroundColor: "white",
    borderRadius: 8,
    paddingVertical: 8,
    elevation: 5,
  },
  selectOption: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  selectOptionText: {
    fontSize: 14,
    color: "#333",
  },
  modalFooter: {
    flexDirection: "row",
    width: "100%",
  },
  modalBtn: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
  },
  modalBtnText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 14,
  },
})